import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Dropdown, Table } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import WorldMap from './WorldMap';
import countriesJson from '@/data/countries.json';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import PlotSkeleton from './PlotSkeleton';

const WORLD_MAP_HEIGHT = 'clamp(14rem, 32vw, 20rem)';

// import { WorldMap } from '@/components/tools/analytics/users-by-country/WorldMap';

const WorldMapCard = ({ data, loading = false }: Record<string, any>) => {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [maxZoomLevel] = useState(5);
  const [minZoomLevel] = useState(1);
  const mapData = transformToMapData(data);
  const hasMapData = mapData.length > 0;

  const handleZoomIn = () => {
    const nextZoomLevel = Math.min(zoomLevel + 1, maxZoomLevel);
    setZoomLevel(nextZoomLevel);
    chartRef.current?.getEchartsInstance()?.setOption({
      series: {
        zoom: nextZoomLevel
      }
    });
  };
  const handleZoomOut = () => {
    const nextZoomLevel = Math.max(zoomLevel - 1, minZoomLevel);
    setZoomLevel(nextZoomLevel);
    chartRef.current?.getEchartsInstance()?.setOption({
      series: {
        zoom: nextZoomLevel
      }
    });
  };
  const handleCountryMouseEnter = countryName => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;

    chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
    chart.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      name: countryName
    });
  };
  const handleCountryMouseLeave = countryName => {
    chartRef.current?.getEchartsInstance()?.dispatchAction({
      type: 'downplay',
      seriesIndex: 0,
      name: countryName
    });
  };
  const cardRef = useRef<HTMLDivElement | null>(null);
  return (
    <Card className="h-100 d-flex flex-column" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            توزیع مکان بک‌لینک‌
            <HelpTooltip html="توزیع دامنه‌های ارجاع‌دهنده بر اساس کشورِ IP آن‌ها." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <Dropdown
            align="end"
            className="btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item className="link-600 fs--1 d-flex align-items-center gap-1 py-1">
                ذخیره تصویر
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  cardRef.current.requestFullscreen();
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 py-1"
              >
                تمام صفحه
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
      <Card.Body
        className="position-relative pb-3 d-flex flex-column flex-grow-1 gap-3"
        style={{ minHeight: 0 }}
      >
        <div className="flex-shrink-0">
          {loading ? (
            <PlotSkeleton height={WORLD_MAP_HEIGHT} />
          ) : (
            <WorldMap
              data={mapData}
              ref={chartRef}
              style={{ height: WORLD_MAP_HEIGHT }}
              maxZoomLevel={maxZoomLevel}
              minZoomLevel={minZoomLevel}
            />
          )}
        </div>
        {!loading && (
          <div className="position-absolute top-0 border mt-3 border-200 rounded-3 bg-body-tertiary">
            <Button
              variant="link"
              size="sm"
              className="bg-100 rounded-bottom-0 px-2 text-700"
              onClick={handleZoomIn}
              disabled={zoomLevel >= maxZoomLevel}
              aria-label="بزرگ‌نمایی نقشه"
            >
              <FontAwesomeIcon icon="plus" className="fs--1" />
            </Button>
            <hr className="m-0 text-200" />
            <Button
              variant="link"
              size="sm"
              className="bg-100 rounded-top-0 px-2 text-700"
              onClick={handleZoomOut}
              disabled={zoomLevel <= minZoomLevel}
              aria-label="کوچک‌نمایی نقشه"
            >
              <FontAwesomeIcon icon="minus" className="fs--1" />
            </Button>
          </div>
        )}
        {loading ? (
          <div className="flex-grow-1" style={{ minHeight: 0 }} />
        ) : hasMapData ? (
          <div
            className="overflow-auto border rounded-3 flex-grow-1"
            role="region"
            aria-label="فهرست کشورهای بک‌لینک"
            tabIndex={0}
            style={{
              minHeight: 0,
              maxHeight: '24rem'
            }}
          >
            <Table hover size="sm" className="mb-0 align-middle w-100">
              <caption className="visually-hidden">
                توزیع دامنه‌های ارجاع‌دهنده بر اساس کشور
              </caption>
              <thead className="bg-body-tertiary sticky-top">
                <tr>
                  <th scope="col">کشور</th>
                  <th scope="col" className="text-end" aria-sort="descending">
                    تعداد
                  </th>
                </tr>
              </thead>
              <tbody>
                {mapData
                  .slice()
                  .sort((a, b) => Number(b.value) - Number(a.value))
                  .map((item, index) => (
                    <tr
                      key={`${item.name}-${index}`}
                      onMouseEnter={() => handleCountryMouseEnter(item.name)}
                      onMouseLeave={() => handleCountryMouseLeave(item.name)}
                    >
                      <td>
                        <div
                          className="d-flex align-items-center gap-2"
                          dir="rtl"
                        >
                          <img
                            className="flex-shrink-0 rounded-1"
                            src={`https://static.mizfa.tools/flags/${item.countryCode}.webp`}
                            alt={`پرچم ${item.name}`}
                            width="24"
                            height="16"
                            loading="lazy"
                          />
                          <span className="text-truncate">{item.name}</span>
                        </div>
                      </td>
                      <td className="text-end" dir="ltr">
                        {Number(item.value).toLocaleString('fa-IR')}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="d-flex flex-grow-1 align-items-center justify-content-center mt-3">
            <div
              className="w-100 border border-200 rounded-2 bg-body-tertiary px-3 py-4 text-center text-600"
              role="status"
            >
              برای نمایش توزیع مکان، داده‌ای وجود ندارد.
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default WorldMapCard;

const transformToMapData = data => {
  const transformedData = [];
  (data ?? []).forEach(item => {
    if (!item?.name) return;
    const countryName = String(item.name).trim();
    const countryCode = countryName.toUpperCase();
    const match = countriesJson.find(
      c =>
        c['alpha-2'] === countryCode ||
        c.name.toLocaleLowerCase() === countryName.toLocaleLowerCase()
    );
    if (!match) return;
    transformedData.push({
      name: match.name,
      value: Number(item.value),
      countryCode: match['alpha-2'].toLowerCase(),
      item
    });
  });
  return transformedData;
};
